import React, { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "react-query";

import { PageLayout, ActionsRow } from "components/PageLayout";
import { BoldText, RegularText } from "components/Text";
import { Button } from "components/Button";
import { Input } from "components/Input";
import { Select } from "components/Select";
import { SimpleList } from "components/SimpleList";
import { VerticalSpace } from "components/Spacing";
import {
  useModal,
  ConfirmationModal,
  InfoModal,
} from "components/Modal";

import { apiGetList, apiPost, apiPut, apiDelete, getErrorMessage } from "services/api";
import { usePriceLists, useArticles } from "shared-apis";

import styles from "./styles.module.scss";

const STORES_KEY = ["stores"];

const getStores = () => apiGetList("stores", { page: 1, pageSize: 1000 });

const StoreForm = ({ store, onSaved }) => {
  const queryClient = useQueryClient();

  const priceListsQuery = usePriceLists();
  const articlesQuery = useArticles();

  const priceListOptions = useMemo(
    () =>
      (priceListsQuery.data?.items ?? []).map((p) => ({
        value: String(p.pricelistid),
        label: p.name,
      })),
    [priceListsQuery.data]
  );

  const articleOptions = useMemo(
    () =>
      (articlesQuery.data?.items ?? []).map((a) => ({
        value: String(a.articleid),
        label: a.name,
      })),
    [articlesQuery.data]
  );

  const [name, setName] = useState("");
  const [pricelistOption, setPricelistOption] = useState(null);
  const [articleOption, setArticleOption] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  // Заполнение формы при смене выбранного склада
  useEffect(() => {
    setError("");
    setName(store?.name ?? "");
  }, [store]);

  useEffect(() => {
    setPricelistOption(
      priceListOptions.find(
        (o) => String(o.value) === String(store?.pricelistid ?? "")
      ) || null
    );
  }, [store, priceListOptions]);

  useEffect(() => {
    setArticleOption(
      articleOptions.find(
        (o) => String(o.value) === String(store?.articleid ?? "")
      ) || null
    );
  }, [store, articleOptions]);

  const handleSave = async () => {
    if (!name.trim() || !pricelistOption || !articleOption) {
      setError("Заполните все поля");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const payload = {
        name: name.trim(),
        pricelistid: Number(pricelistOption.value),
        articleid: Number(articleOption.value),
      };
      if (store) {
        await apiPut("stores", { ...payload, storesid: store.storesid });
      } else {
        await apiPost("stores", payload);
      }
      queryClient.invalidateQueries(STORES_KEY);
      onSaved();
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageLayout
      className={styles.rightPanel}
      header={<BoldText>{store ? store.name : "Новый магазин"}</BoldText>}
      footer={
        <ActionsRow>
          {!!error && (
            <RegularText size={13} color="danger" className={styles.footerError}>
              {error}
            </RegularText>
          )}
          <Button
            icon="galochka"
            variant="secondary"
            onClick={handleSave}
            loading={saving}
          >
            Сохранить
          </Button>
        </ActionsRow>
      }
    >
      <div className={styles.form}>
        {!!store && (
          <>
            <Input
              label="Код"
              labelWidth={200}
              value={store.storesid}
              disabled
              inputWidth={300}
            />
            <VerticalSpace size={12} />
          </>
        )}
        <Input
          label="Название"
          labelWidth={200}
          value={name}
          onChange={(ev) => setName(ev.target.value)}
          inputWidth={300}
        />
        <VerticalSpace size={12} />
        <Select
          label="Прайс лист"
          labelWidth={200}
          options={priceListOptions}
          value={pricelistOption}
          onChange={setPricelistOption}
          isLoading={priceListsQuery.isLoading}
          width={300}
        />
        <VerticalSpace size={12} />
        <Select
          label="Статья затрат при продаже"
          labelWidth={200}
          options={articleOptions}
          value={articleOption}
          onChange={setArticleOption}
          isLoading={articlesQuery.isLoading}
          width={300}
        />
      </div>
    </PageLayout>
  );
};

export const StocksPage = () => {
  const queryClient = useQueryClient();
  const { openModal, closeModal } = useModal();

  const storesQuery = useQuery(STORES_KEY, getStores);
  const stores = storesQuery.data?.items ?? [];

  const [activeStore, setActiveStore] = useState(null);

  // Автовыбор первого склада после загрузки
  useEffect(() => {
    if (!activeStore && stores.length) setActiveStore(stores[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stores]);

  const onDeleteClick = () => {
    if (!activeStore) return;
    openModal(
      <ConfirmationModal
        title={`Вы действительно хотите удалить склад/магазин "${activeStore.name}"?`}
        onConfirm={async () => {
          try {
            await apiDelete("stores", {
              storesid: Number(activeStore.storesid),
            });
            setActiveStore(null);
            queryClient.invalidateQueries(STORES_KEY);
            closeModal();
          } catch (e) {
            openModal(
              <InfoModal title={getErrorMessage(e)} onClose={closeModal} />
            );
          }
        }}
        onClose={closeModal}
      />
    );
  };

  return (
    <div className={styles.page}>
      <PageLayout
        className={styles.leftPanel}
        header={<BoldText>Склады, магазины</BoldText>}
        footer={
          <ActionsRow>
            <Button
              icon="plus"
              onClick={() => setActiveStore(null)}
              title="Добавить склад/магазин"
            />
            <Button
              icon="delete"
              onClick={onDeleteClick}
              disabled={!activeStore}
              title="Удалить"
            />
          </ActionsRow>
        }
      >
        <SimpleList
          items={stores}
          rowKey="storesid"
          activeId={activeStore?.storesid}
          loading={storesQuery.isLoading}
          error={storesQuery.isError ? getErrorMessage(storesQuery.error) : ""}
          onRetry={storesQuery.refetch}
          onRowClick={setActiveStore}
        />
      </PageLayout>
      <StoreForm store={activeStore} onSaved={() => setActiveStore(null)} />
    </div>
  );
};
